import React, { memo, useCallback, useRef, useState } from 'react';

import AcceptDeclineIconButtons from '@components/common/AcceptDeclineIconButtons/AcceptDeclineIconButtons';
import DynamicIconWrapper from '@components/common/DynamicIconWrapper';

import { Icons } from '@icons/icons';

import { ADD_OBJECT_NODE_ERROR } from '@utils/treeValidation';

import { TEmptyObject } from '@interfaces/common/common';
import { IBasicNode } from '@interfaces/components/tree';
import { IAddXMLNode } from '@components/common/FlatXMLTree/interface';

import {
	IFlatTreeNodeValidation,
	TFlatTreeGetValidation,
} from '../../interface';
import FlatTreeInput from '../FlatTreeInput';
import FlatTreeNodeError from '../FlatTreeNodeError';
import FlatTreeSelect from '../FlatTreeSelect';
import AddFlatNodeValuesForm from './AddFlatNodeValuesForm';

import styles from './AddFlatNodeForm.module.scss';

const KEY_ALREADY_EXISTS_ERROR = 'Key already exists';
const INFO_ICON_SIZE = '16px';

interface AddFlatNodeFormProps {
	onSubmit: (data: IAddXMLNode<IBasicNode>) => void;
	exitAddMode: () => void;
	isArray: boolean;
	node: IBasicNode;
	availableValues?: string[];
	parentNodeValidation: TEmptyObject | IFlatTreeNodeValidation;
	getValidationRules?: TFlatTreeGetValidation;
	usedKeys?: string[];
	isNoKeysAvailable?: boolean;
	getChildrenNamesList?: <T extends IBasicNode = any>(node: T) => string[];
}

const AddFlatNodeForm = ({
	onSubmit,
	exitAddMode,
	getValidationRules,
	isArray,
	node,
	usedKeys,
	isNoKeysAvailable,
	parentNodeValidation,
}: AddFlatNodeFormProps) => {
	const [key, setKey] = useState<string | undefined>('');
	const [value, setValue] = useState<string | undefined>('');
	const [nodeValidation, setNodeValidation] = useState<IFlatTreeNodeValidation>();
	const [error, setError] = useState<string | null>(null);

	const { availableValues, availableTypes } = parentNodeValidation;
	const selectRef = useRef<HTMLSelectElement>(null);
	const selectValueRef = useRef<HTMLSelectElement>(null);

	const {
		validateValue,
		validateInput,
		requirements,
		errorText = 'Invalid value',
		availableValues: nodeAvailableValues,
	} = nodeValidation ? nodeValidation : parentNodeValidation;

	const isSelectMode = availableValues?.length > 0;
	const isInputMode = !isSelectMode && !isArray;
	const isAvailableValuesMode = nodeAvailableValues?.length > 0;

	const nodeType = selectRef.current?.value && availableTypes?.length && isAvailableValuesMode
		? availableTypes.find((type) => type.name === selectRef.current?.value)?.type?.name
		: undefined;
	const getValidation = useCallback(async (selectValue: string) => {
		if (getValidationRules && node?.description && selectValue) {
			try {
				const { validations } = await getValidationRules({
					name: selectValue,
					nameSpace: node.description,
					path: node.keysPath || '',
				});

				setNodeValidation(validations);
			} catch {
				setNodeValidation(undefined);
			}
		}
	}, [
		getValidationRules,
		node.description,
		node.keysPath,
	]);

	const addHandler = useCallback(() => {
		const name = isSelectMode ? selectRef.current?.value : key;

		const nodeValue = isAvailableValuesMode ? selectValueRef.current?.value : value;

		const isValueError = !nodeType
			? validateValue
				? nodeValue && !validateValue(nodeValue)
				: false
			: false;

		const isKeyExist = usedKeys?.length && name ? usedKeys.includes(name) : false;
		const isValid = !nodeType
			? isArray
				? !!nodeValue
				: !!name && !!nodeValue
			: true;

		if (isValid && !isValueError && !isKeyExist) {
			onSubmit({
				parent: node,
				value: nodeValue!,
				key: name,
				nodeType,
			});

			exitAddMode();
			setError(null);
		} else {
			setError(isKeyExist ? KEY_ALREADY_EXISTS_ERROR : errorText);
		}
	}, [
		isArray,
		usedKeys,
		node,
		nodeType,
		selectRef,
		selectValueRef,
		isAvailableValuesMode,
		isSelectMode,
		key,
		value,
		errorText,
		onSubmit,
		exitAddMode,
		validateValue,
	]);

	const isErrorVisible = !nodeType
		? isSelectMode && !!availableValues && availableValues.length === 0 || isNoKeysAvailable
		: false;

	return !isErrorVisible
		? (
			<div
				className={styles.wrapper}
				data-testid='add_node'
			>
				{error && <FlatTreeNodeError errorText={error} />}
				{isSelectMode && (
					<FlatTreeSelect
						onChange={getValidation}
						options={availableValues}
						selectRef={selectRef}
					/>
				)}
				{isInputMode && (
					<FlatTreeInput
						inputValue={key}
						setInputValue={setKey}
						testName='add_node_key'
					/>
				)}
				{requirements &&
					<span className={styles.requirements}>
						<DynamicIconWrapper
							icon={Icons.info}
							iconStyle={{
								width: INFO_ICON_SIZE,
								height: INFO_ICON_SIZE,
							}}
							tooltipLabel={requirements}
						/>
					</span>
				}
				{!nodeType
					? (
						<AddFlatNodeValuesForm
							isAvailableValuesMode={isAvailableValuesMode}
							nodeAvailableValues={nodeAvailableValues || []}
							selectValueRef={selectValueRef}
							setValue={setValue}
							validateInput={validateInput}
							value={value}
						/>
					) : null
				}
				<AcceptDeclineIconButtons
					negativeHandler={exitAddMode}
					positiveHandler={addHandler}
				/>
			</div>
		) : (
			<FlatTreeNodeError
				errorText={availableValues && isSelectMode || isNoKeysAvailable
					? ADD_OBJECT_NODE_ERROR
					: errorText
				}
			/>
		);
};

export default memo(AddFlatNodeForm);
